import { useTranslation } from "react-i18next";
import { activeFilterChip } from "../../components/common/ActiveFilterBar/ActiveFilterBar.js";
import { messageTypeLabelKey } from "./incoming-report-presentation.js";
import { platformLabelKey } from "./outgoing-report-presentation.js";
import {
  countIncomingActiveFilters,
  IncomingReportFilters,
  type IncomingReportFilterValues,
} from "./IncomingReportFilters.js";
import type { IncomingReportQuery } from "./incoming-report-query.js";
import { useReportFiltersSheet } from "./ReportFilterSheet.js";
import { ReportToolbarRow } from "./ReportToolbarRow.js";

export function IncomingReportToolbar({
  query,
  dateFrom,
  dateTo,
  options,
  filteredTotal,
  filtersActive,
  onSearchChange,
  onDateRangeChange,
  onFiltersChange,
  onResetFilters,
  columnsSlot,
}: {
  query: IncomingReportQuery;
  dateFrom: string;
  dateTo: string;
  options: Parameters<typeof IncomingReportFilters>[0]["options"];
  filteredTotal: number | null;
  filtersActive: boolean;
  onSearchChange: (value: string) => void;
  onDateRangeChange: (from: string, to: string) => void;
  onFiltersChange: (patch: Partial<IncomingReportFilterValues>) => void;
  onResetFilters: () => void;
  columnsSlot?: React.ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { filtersOpen, setFiltersOpen } = useReportFiltersSheet();
  const filterValues: IncomingReportFilterValues = {
    platform: query.platform,
    endpointId: query.endpointId,
    messageType: query.messageType,
  };
  const activeCount = countIncomingActiveFilters(filterValues);
  const endpoint = options?.endpoints.find((item) => item.endpointId === query.endpointId);
  const filterChips = [
    activeFilterChip(
      "platform",
      t("reports.incoming.filters.channel"),
      query.platform ? t(platformLabelKey(query.platform)) : null,
      () => onFiltersChange({ platform: undefined }),
    ),
    activeFilterChip(
      "endpoint",
      t("reports.incoming.filters.endpoint"),
      endpoint
        ? endpoint.secondaryLabel
          ? `${endpoint.displayLabel} · ${endpoint.secondaryLabel}`
          : endpoint.displayLabel
        : null,
      () => onFiltersChange({ endpointId: undefined }),
    ),
    activeFilterChip(
      "messageType",
      t("reports.incoming.filters.messageType"),
      query.messageType ? t(messageTypeLabelKey(query.messageType)) : null,
      () => onFiltersChange({ messageType: undefined }),
    ),
  ].flatMap((chip) => (chip ? [chip] : []));

  const filters = (
    <IncomingReportFilters
      options={options}
      values={filterValues}
      onChange={onFiltersChange}
      idPrefix="reports-incoming-more"
    />
  );

  return (
    <ReportToolbarRow
      toolbarTestId="reports-incoming-toolbar"
      search={query.search}
      onSearchChange={onSearchChange}
      searchPlaceholder={t("reports.incoming.searchPlaceholder")}
      searchTestId="reports-incoming-search"
      dateFrom={dateFrom}
      dateTo={dateTo}
      onDateRangeChange={onDateRangeChange}
      fromTestId="reports-incoming-from"
      toTestId="reports-incoming-to"
      columnsSlot={columnsSlot}
      moreFilters={filters}
      moreFiltersCount={activeCount}
      moreFiltersTestId="reports-incoming-more-filters"
      mobileFiltersOpen={filtersOpen}
      onMobileFiltersOpenChange={setFiltersOpen}
      mobileFiltersTitle={t("reports.incoming.filtersTitle")}
      mobileFiltersDescription={t("reports.incoming.filtersDescription")}
      mobileFiltersTestId="reports-incoming-filters-sheet"
      mobileFiltersOpenTestId="reports-incoming-open-filters"
      mobileFiltersBody={
        <IncomingReportFilters
          options={options}
          values={filterValues}
          onChange={onFiltersChange}
          idPrefix="reports-incoming-sheet"
        />
      }
      filtersActive={filtersActive}
      explicitDateFrom={query.from}
      explicitDateTo={query.to}
      activeFilters={filterChips}
      onResetFilters={onResetFilters}
      resetTestId="reports-incoming-reset"
      filteredCountSlot={
        filteredTotal != null ? (
          <p
            className="m-0 flex h-8 shrink-0 items-center whitespace-nowrap text-[0.75rem] text-muted"
            data-testid="reports-incoming-filtered-count"
          >
            {t("reports.incoming.filteredCount", {
              formatted: filteredTotal.toLocaleString(i18n.language),
            })}
          </p>
        ) : null
      }
    />
  );
}
